import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, untracked } from '@angular/core';

import { WorkflowConsent } from '@shared/models';

/**
 * The consents a customer owes on a screen, as Document Acceptance returns them (KOR-15237): one checkbox per
 * consent, its notice above it and every document it covers linked under it. All text is shown exactly as sent,
 * as plain text. Reports which consents are ticked; deciding what that allows is the screen's job.
 */
@Component({
  selector: 'app-consent-list',
  templateUrl: './consent-list.component.html',
  styleUrls: ['./consent-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ConsentListComponent {
  consents = input.required<WorkflowConsent[]>();

  tickedChange = output<WorkflowConsent[]>();

  ordered = computed(() => [...this.consents()].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)));

  /** Keyed by presentation token: a fresh set from DAD carries new tokens, so nothing ticked before carries over. */
  private tickedTokens = signal<ReadonlySet<string>>(new Set());

  constructor() {
    // A fresh set (e.g. after an expired token): tell the screen nothing is ticked any more.
    effect(
      () => {
        this.consents();
        untracked(() => {
          this.tickedTokens.set(new Set());
          this.tickedChange.emit([]);
        });
      },
      { allowSignalWrites: true },
    );
  }

  isTicked = (consent: WorkflowConsent): boolean => this.tickedTokens().has(consent.presentationToken);

  isRequired = (consent: WorkflowConsent): boolean => consent.required !== false;

  toggle = (consent: WorkflowConsent, checked: boolean): void => {
    const tokens = new Set(this.tickedTokens());
    if (checked) {
      tokens.add(consent.presentationToken);
    } else {
      tokens.delete(consent.presentationToken);
    }
    this.tickedTokens.set(tokens);
    this.tickedChange.emit(this.ordered().filter(({ presentationToken }) => tokens.has(presentationToken)));
  };

  inputId = (index: number): string => `consent-${index}`;

  noticeId = (index: number): string => `consent-${index}-notice`;
}
